import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { formatCurrency } from '../../../utils/formatters';

export type LibroIvaResumenTotales = {
  netoGravado: number;
  iva21: number;
  iva105: number;
  iva27: number;
  exento: number;
  total: number;
};

export type LibroIvaResumenGrupo = LibroIvaResumenTotales & {
  clave: string;
  cantidad: number;
};

export const libroIvaFooterCellSx = { fontWeight: 700, bgcolor: '#f0f4f8', borderTop: '2px solid #bdbdbd' };

const headCellSx = { fontWeight: 600 };

type LibroIvaResumenAccordionProps = {
  titulo: string;
  descripcion: string;
  icon: React.ReactNode;
  expanded: boolean;
  onChange: (expanded: boolean) => void;
  accion?: React.ReactNode;
  children: React.ReactNode;
};

export const LibroIvaResumenAccordion: React.FC<LibroIvaResumenAccordionProps> = ({
  titulo,
  descripcion,
  icon,
  expanded,
  onChange,
  accion,
  children,
}) => (
  <Accordion
    variant="outlined"
    disableGutters
    expanded={expanded}
    onChange={(_, value) => onChange(value)}
    TransitionProps={{ unmountOnExit: true }}
    sx={{
      mb: 2,
      borderRadius: 2,
      '&:before': { display: 'none' },
      boxShadow: 'none',
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      sx={{ bgcolor: '#fafafa', minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ minWidth: 0, pr: 1, flex: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          {icon}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {titulo}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {descripcion}
            </Typography>
          </Box>
        </Stack>
        {accion ? (
          <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
            {accion}
          </Box>
        ) : null}
      </Stack>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 2, pb: 2 }}>{children}</AccordionDetails>
  </Accordion>
);

type LibroIvaResumenAgrupadoTableProps = {
  columnaClave: string;
  grupos: LibroIvaResumenGrupo[] | null;
  loading: boolean;
  claveSeleccionada?: string | null;
  onToggleClave?: (clave: string) => void;
};

export const LibroIvaResumenAgrupadoTable: React.FC<LibroIvaResumenAgrupadoTableProps> = ({
  columnaClave,
  grupos,
  loading,
  claveSeleccionada = null,
  onToggleClave,
}) => {
  if (loading || grupos === null) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const totales = grupos.reduce(
    (acc, g) => ({
      cantidad: acc.cantidad + g.cantidad,
      netoGravado: acc.netoGravado + g.netoGravado,
      iva21: acc.iva21 + g.iva21,
      iva105: acc.iva105 + g.iva105,
      iva27: acc.iva27 + g.iva27,
      exento: acc.exento + g.exento,
      total: acc.total + g.total,
    }),
    { cantidad: 0, netoGravado: 0, iva21: 0, iva105: 0, iva27: 0, exento: 0, total: 0 }
  );

  return (
    <TableContainer
      component={Paper}
      sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0', overflowX: 'auto' }}
    >
      <Table size="small" sx={{ minWidth: 800 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
            <TableCell sx={headCellSx}>{columnaClave}</TableCell>
            <TableCell align="right" sx={headCellSx}>Comprobantes</TableCell>
            <TableCell align="right" sx={headCellSx}>Neto Gravado</TableCell>
            <TableCell align="right" sx={headCellSx}>IVA 1 (21%)</TableCell>
            <TableCell align="right" sx={headCellSx}>IVA 2 (10.5%)</TableCell>
            <TableCell align="right" sx={headCellSx}>IVA 27%</TableCell>
            <TableCell align="right" sx={headCellSx}>Exento</TableCell>
            <TableCell align="right" sx={headCellSx}>Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {grupos.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center">
                Sin datos para el período seleccionado.
              </TableCell>
            </TableRow>
          ) : (
            grupos.map((g) => {
              const seleccionada = claveSeleccionada === g.clave;
              return (
              <TableRow
                key={g.clave}
                hover
                selected={seleccionada}
                onClick={onToggleClave ? () => onToggleClave(g.clave) : undefined}
                sx={onToggleClave ? { cursor: 'pointer' } : undefined}
              >
                <TableCell sx={seleccionada ? { fontWeight: 600 } : undefined}>{g.clave}</TableCell>
                <TableCell align="right">{g.cantidad}</TableCell>
                <TableCell align="right">{formatCurrency(g.netoGravado)}</TableCell>
                <TableCell align="right">{formatCurrency(g.iva21)}</TableCell>
                <TableCell align="right">{formatCurrency(g.iva105)}</TableCell>
                <TableCell align="right">{formatCurrency(g.iva27)}</TableCell>
                <TableCell align="right">{formatCurrency(g.exento)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {formatCurrency(g.total)}
                </TableCell>
              </TableRow>
              );
            })
          )}
          {grupos.length > 0 ? (
            <TableRow>
              <TableCell sx={libroIvaFooterCellSx}>Total</TableCell>
              <TableCell align="right" sx={libroIvaFooterCellSx}>{totales.cantidad}</TableCell>
              <TableCell align="right" sx={libroIvaFooterCellSx}>{formatCurrency(totales.netoGravado)}</TableCell>
              <TableCell align="right" sx={libroIvaFooterCellSx}>{formatCurrency(totales.iva21)}</TableCell>
              <TableCell align="right" sx={libroIvaFooterCellSx}>{formatCurrency(totales.iva105)}</TableCell>
              <TableCell align="right" sx={libroIvaFooterCellSx}>{formatCurrency(totales.iva27)}</TableCell>
              <TableCell align="right" sx={libroIvaFooterCellSx}>{formatCurrency(totales.exento)}</TableCell>
              <TableCell align="right" sx={libroIvaFooterCellSx}>{formatCurrency(totales.total)}</TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
